import {
  AsyncPaginate,
  ComponentProps,
  UseAsyncPaginateParams,
  reduceGroupedOptions,
  withAsyncPaginate,
} from 'react-select-async-paginate';
import React, { ReactElement, useCallback, useId, useRef } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import Creatable, { CreatableProps } from 'react-select/creatable';
import { useTranslation } from 'react-i18next';
import { isObservable, Observable } from 'rxjs';
import { emptySelectOption, Option } from '../select';
import { GroupBase, MenuPlacement, MenuPosition } from 'react-select';

type AsyncPaginateCreatableProps<
  OptionType,
  Group extends GroupBase<OptionType>,
  Additional,
  IsMulti extends boolean
> = CreatableProps<OptionType, IsMulti, Group> &
  UseAsyncPaginateParams<OptionType, Group, Additional> &
  ComponentProps<OptionType, Group, IsMulti>;

type AsyncPaginateCreatableType = <
  OptionType,
  Group extends GroupBase<OptionType>,
  Additional,
  IsMulti extends boolean = false
>(
  props: AsyncPaginateCreatableProps<OptionType, Group, Additional, IsMulti>
) => ReactElement;

const CreatableAsyncPaginate = withAsyncPaginate(
  Creatable
) as AsyncPaginateCreatableType;

export type PagedOptions = {
  options: Option[];
  hasMore: boolean;
  additional: { page: number } | any;
};

export type ObservableOptions = (
  value: string,
  loadedOptions: Option[],
  { page }: any
) => Observable<PagedOptions> | Promise<PagedOptions>;

export type AsyncSelectType = {
  name: string;
  parentName: string;
  cacheOn: string[] | ((parentName: string) => string[]);
  disabled: boolean;
  multi?: boolean;
  creatable?: boolean;
  clearable?: boolean;
  grouped?: boolean;
  asyncOptions: ObservableOptions;
  menuPlacement?: MenuPlacement;
  menuPosition?: MenuPosition;
};

const additional = { page: 1 };
const theme = {
  borderRadius: 0,
  colors: {
    primary: '',
    primary75: '',
    primary50: '',
    primary25: '',
    danger: '',
    dangerLight: '',
    neutral0: '',
    neutral5: '',
    neutral10: '',
    neutral20: '',
    neutral30: '',
    neutral40: '',
    neutral50: '',
    neutral60: '',
    neutral70: '',
    neutral80: '',
    neutral90: '',
  },
  spacing: { baseUnit: 0, controlHeight: 0, menuGutter: 0 },
};

export default function AsyncSelect({
  name,
  parentName,
  cacheOn,
  disabled,
  multi,
  creatable,
  clearable,
  grouped,
  asyncOptions,
  menuPlacement,
  menuPosition,
}: AsyncSelectType) {
  const Select = creatable ? CreatableAsyncPaginate : AsyncPaginate;
  const { getValues, setValue } = useFormContext();
  const { t } = useTranslation();
  const groupedOptionsFetchCount = useRef(1);
  const id = useId();

  const loading = useCallback(() => t('Loading'), [t]);
  const noOptions = useCallback(() => t('No options'), [t]);
  const formatCreateLabel = useCallback(
    (v: string) => `${t('create')} "${v}"`,
    [t]
  );

  if (parentName && typeof cacheOn === 'object') {
    cacheOn = cacheOn.map((name) => {
      switch (true) {
        case name.startsWith('/'):
          return name.substring(1);
        case name.startsWith('_'):
          return name.substring(1).replace(/__(\d+)__/g, (match, index) => {
            return '.' + parentName.split('.')[index] + '.';
          });
        default:
          return `${parentName}.${name}`;
      }
    });
  }

  cacheOn = typeof cacheOn === 'function' ? cacheOn(parentName) : cacheOn ?? [];

  const wildcardCacheOn = [];
  const cacheOnWithWildcards = cacheOn
    .map((cacheOnValue) => {
      if (cacheOnValue.includes('.') && cacheOnValue.includes('*')) {
        let separatedCacheOnValue = cacheOnValue.split('*');
        let cacheOnValuePath = getValues(separatedCacheOnValue[0]);

        for (let i = 0; i < (cacheOnValuePath?.length ?? 0); i++) {
          wildcardCacheOn.push(
            `${separatedCacheOnValue[0]}.${i}.${separatedCacheOnValue[1]}`
          );
        }

        return null;
      }
      return cacheOnValue;
    })
    .filter(Boolean)
    .concat(wildcardCacheOn);

  const cacheValues = useWatch({ name: cacheOnWithWildcards as string[] }).map(
    (cacheValue) => {
      if (Array.isArray(cacheValue)) {
        return cacheValue.length;
      }
      return cacheValue;
    }
  );

  const selectName = parentName ? `${parentName}.${name}` : name;
  const {
    field: { value, onBlur, onChange },
  } = useController({
    name: selectName,
    shouldUnregister: false,
    defaultValue: multi ? undefined : emptySelectOption,
  });

  const customOnChange = useCallback(
    (event: any) => {
      onBlur();
      onChange(event ?? emptySelectOption);
    },
    [onBlur, onChange]
  );

  const promiseLoadOptions: any = useCallback(
    (search: string, loadedOptions: Option[], additional: any) => {
      const loadOptions = asyncOptions(search, loadedOptions, {
        ...additional,
        parentName,
        getValues,
        setValue,
      });

      if (isObservable(loadOptions)) {
        return new Promise<PagedOptions>((resolve, reject) => {
          loadOptions.subscribe(
            (response) => {
              resolve(response);
            },
            (error) => {
              console.error(error);

              reject(error);
            }
          );
        });
      } else {
        return loadOptions.catch((error) => console.error(error));
      }
    },
    [asyncOptions, getValues, setValue, parentName]
  );

  if (cacheValues.length && cacheValues.some((v) => v === undefined)) {
    return null;
  }

  const shouldLoadMore = (
    _scrollHeight: number,
    clientHeight: number,
    scrollTop: number
  ) => {
    const isEndOfThreshold =
      clientHeight * groupedOptionsFetchCount.current < scrollTop;

    if (isEndOfThreshold) {
      groupedOptionsFetchCount.current += 1;

      return true;
    }

    return false;
  };

  return (
    <div data-name={selectName}>
      <Select
        key={id}
        debounceTimeout={500}
        additional={additional}
        {...(grouped ? { shouldLoadMore } : {})}
        value={value}
        defaultOptions
        isMulti={multi}
        isClearable={clearable}
        isDisabled={disabled}
        reduceOptions={grouped ? reduceGroupedOptions : undefined}
        onBlur={onBlur}
        name={selectName}
        classNamePrefix={'rs'}
        placeholder={''}
        menuPosition={menuPosition}
        menuPlacement={menuPlacement}
        theme={theme}
        loadingMessage={loading}
        noOptionsMessage={noOptions}
        onChange={customOnChange}
        cacheUniqs={cacheValues.length ? cacheValues : undefined}
        loadOptions={promiseLoadOptions}
        formatCreateLabel={formatCreateLabel}
      />
    </div>
  );
}
